import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  DashboardIcon,
  CatalogIcon,
  ReportsIcon,
  ManagementIcon,
  BellIcon,
  SettingsIcon,
  HelpIcon,
  UserMgmtIcon,
  MyOffersIcon,
  LogoutIcon,
  PlusIcon,
} from '../ui/Icons';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  adminOnly?: boolean;
}

const topNavItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', Icon: DashboardIcon as LucideIcon },
  { to: '/catalog', label: 'Product Catalog', Icon: CatalogIcon as LucideIcon },
  { to: '/my-offers', label: 'My Offers', Icon: MyOffersIcon as LucideIcon },
  { to: '/reports', label: 'Reports', Icon: ReportsIcon as LucideIcon },
  { to: '/reseller-management', label: 'Reseller Mgmt', Icon: ManagementIcon as LucideIcon, adminOnly: true },
  { to: '/user-management', label: 'User Management', Icon: UserMgmtIcon as LucideIcon, adminOnly: true },
];

const bottomNavItems: NavItem[] = [
  { to: '/notifications', label: 'Notifications', Icon: BellIcon as LucideIcon },
  { to: '/settings', label: 'Settings', Icon: SettingsIcon as LucideIcon },
  { to: '/help', label: 'Help & Support', Icon: HelpIcon as LucideIcon },
];

export const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const visibleTop = topNavItems.filter(
    (item) => !item.adminOnly || user?.role === 'reseller-admin',
  );

  const initials = user?.name
    ? user.name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()
    : 'U';

  return (
    <aside style={{
      width: '232px',
      height: '100vh',
      background: 'var(--sidebar-bg)',
      borderRight: '1px solid var(--sidebar-border)',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      {/* Logo */}
      <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <img src="/salam-logo-light.svg" alt="Salam" style={{ height: '30px', display: 'block' }} />
        <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '3px', fontWeight: 500 }}>
          Reseller Portal
        </div>
      </div>

      {/* New Offer CTA */}
      <div style={{ padding: '12px 12px 8px' }}>
        <button
          onClick={() => navigate('/offers/wizard/location')}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: 'var(--p)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontFamily: 'inherit',
            fontSize: '13.5px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'background .15s, box-shadow .15s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--p-hover)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--p)'; }}
        >
          <PlusIcon size={15} />
          New Offer
        </button>
      </div>

      {/* Primary nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
        {visibleTop.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/catalog' || item.to === '/my-offers'}
            className={({ isActive }) => ['nav-item', isActive ? 'active' : ''].filter(Boolean).join(' ')}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <>
                <item.Icon size={16} color={isActive ? 'var(--p)' : 'var(--text-2)'} />
                {item.label}
              </>
            )}
          </NavLink>
        ))}

        <div style={{ height: '1px', background: 'var(--border)', margin: '8px 4px' }} />

        {bottomNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => ['nav-item', isActive ? 'active' : ''].filter(Boolean).join(' ')}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <>
                <item.Icon size={16} color={isActive ? 'var(--p)' : 'var(--text-2)'} />
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid var(--border)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <div style={{
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          background: 'var(--p-light)',
          color: 'var(--p)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '13px',
          flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.name ?? '—'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.role ?? 'reseller'}
          </div>
        </div>
        <button
          onClick={logout}
          title="Sign Out"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', display: 'flex', padding: '4px', borderRadius: '6px', flexShrink: 0 }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--err)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'var(--text-2)'; }}
        >
          <LogoutIcon size={16} />
        </button>
      </div>
    </aside>
  );
};
